import { useEffect, useRef } from "react";
import { motion, useInView, animate } from "framer-motion";

function formatNumber(value, { decimals = 0, useCommas = false } = {}) {
  const fixed = Number(value).toFixed(decimals);
  if (!useCommas) return fixed;
  const [intPart, decPart] = fixed.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart ? `${withCommas}.${decPart}` : withCommas;
}

export function AnimatedNumber({
  value,
  decimals = 0,
  useCommas = false,
  duration = 1.4,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });

  useEffect(() => {
    if (!isInView) return;
    const node = ref.current;
    if (!node || value == null || isNaN(value)) return;

    const target = Number(value);
    node.textContent = formatNumber(0, { decimals, useCommas });

    const controls = animate(0, target, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => {
        node.textContent = formatNumber(latest, { decimals, useCommas });
      },
    });
    return () => controls.stop();
  }, [isInView, value, decimals, useCommas, duration]);

  return (
    <span ref={ref}>{formatNumber(0, { decimals, useCommas })}</span>
  );
}

const fadeInText = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
};

export function AnimatedSpec({ value }) {
  if (value == null || value === "") return <span>—</span>;

  if (typeof value === "number") {
    const decimals = Number.isInteger(value)
      ? 0
      : String(value).split(".")[1]?.length || 0;
    return (
      <AnimatedNumber value={value} decimals={decimals} useCommas={false} />
    );
  }

  const str = String(value);
  const match = str.match(/^([\d,]+(?:\.\d+)?)(\s*.*)$/);

  if (match) {
    const numStr = match[1];
    const suffix = (match[2] || "").trim();
    const suffixHasDigit = /\d/.test(suffix);
    const num = parseFloat(numStr.replace(/,/g, ""));

    if (!suffixHasDigit && !isNaN(num)) {
      const decimals = numStr.includes(".")
        ? numStr.split(".")[1].length
        : 0;
      const useCommas = numStr.includes(",");
      return (
        <>
          <AnimatedNumber
            value={num}
            decimals={decimals}
            useCommas={useCommas}
          />
          {suffix && <span className="spec-unit">{suffix}</span>}
        </>
      );
    }
  }

  return (
    <motion.span
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={fadeInText}
      style={{ display: "inline-block" }}
    >
      {str}
    </motion.span>
  );
}
